import { TOGGLE_DRAWER } from "./constants";
import { callAPI } from "./apiUtils";
import {
  FROM_FROROIEGN_PROFILE,
  GROUPS,
  OPEN_CHATS,
  OPEN_PROJECTS,
  RIGHT_TOP,
  SET_CHAT_FULLSCREEN,
  SET_DEEPLINK_DATA,
  SET_DEEPLINK_LOADING,
  SET_IN_ACTIVITY,
  SET_OPEN_SIDE_BAR,
  TOGGLE_CHAT,
  TOGGLE_LATEST_MSG,
  TOGGLE_PROJECT,
  TOGGLE_TODO_VIEW,
  TOGGLE_WORK_AREA,
  UPDATE_PROJECT_ACTION_FIRE,
} from "../store";

const BRANCH_HOST_RE = /(^|\.)gvnhm\.app\.link$/i;
const DEEPLINK_PATH_RE = /\/deeplink\/?$/i;

function isBranchHost(hostname) {
  return BRANCH_HOST_RE.test(String(hostname || ""));
}

function chatParamsFromSearch(qs) {
  if (!qs) return null;
  const desktopUrl = qs.get("$desktop_url") || qs.get("desktop_url");
  if (desktopUrl) {
    const nested = parseChatDeeplinkFromHref(desktopUrl);
    if (nested) return nested;
  }
  const model = String(qs.get("model") || "").toLowerCase();
  if (model === "article" || model === "open") return null;
  const chatGroupId =
    qs.get("chatGroupId") ||
    ((model === "one" || model === "group") ? qs.get("id") : null);
  if (!chatGroupId) return null;
  const companyId = qs.get("companyId") || undefined;
  const roomId = qs.get("roomId") || qs.get("building") || undefined;
  const messageId = qs.get("messageId") || qs.get("chat") || undefined;
  const page_no = qs.get("page_no") || qs.get("page") || undefined;
  return {
    chatGroupId: String(chatGroupId),
    messageId: messageId ? String(messageId) : undefined,
    roomId: roomId ? String(roomId) : undefined,
    companyId: companyId ? String(companyId) : undefined,
    isInternal: !!(companyId || qs.get("isInternal") === "true"),
    page_no: page_no ? Number(page_no) || page_no : undefined,
  };
}

function tryUrl(href) {
  try {
    return new URL(href, typeof window !== "undefined" ? window.location.origin : "https://web.kjuup.de");
  } catch (_) {
    return null;
  }
}

/**
 * Pull chat deeplink ids out of a notification-email CTA (web /deeplink,
 * Branch long/short links with query params, or a nested $desktop_url).
 */
export function parseChatDeeplinkFromHref(href) {
  if (!href || typeof href !== "string") return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) return null;

  const direct = tryUrl(trimmed);
  if (direct) {
    const wrapped = direct.searchParams.get("url") || direct.searchParams.get("u");
    if (wrapped && wrapped !== trimmed) {
      const nestedWrap = parseChatDeeplinkFromHref(wrapped);
      if (nestedWrap) return nestedWrap;
    }
    const path = direct.pathname || "";
    if (DEEPLINK_PATH_RE.test(path) || isBranchHost(direct.hostname)) {
      const parsed = chatParamsFromSearch(direct.searchParams);
      if (parsed) return parsed;
    }
  }

  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch (_) {}
  const nested = decoded.match(/https?:\/\/[^\s"'<>]*\/deeplink\?[^"'<>\s]*/i);
  if (nested && nested[0] !== trimmed) {
    return parseChatDeeplinkFromHref(nested[0]);
  }
  return null;
}

/** True for Kjuup chat-open links, including Branch short URLs without visible ids. */
export function isKjuupChatOpenHref(href) {
  if (parseChatDeeplinkFromHref(href)) return true;
  const url = tryUrl(href);
  if (!url) return false;
  return isBranchHost(url.hostname) || DEEPLINK_PATH_RE.test(url.pathname || "");
}

/**
 * Intercept CTA clicks inside the sandboxed email iframe so "Show message"
 * opens the chat in-app instead of a blocked/popup Branch tab.
 */
async function resolveChatHref(href) {
  const local = parseChatDeeplinkFromHref(href);
  if (local && local.chatGroupId) return local;
  if (!isKjuupChatOpenHref(href)) return null;
  try {
    const { data } = await callAPI("/email/chat-link", { url: href }, "GET");
    if (!data || !data.status || !data.chatGroupId) {
      return data && data.href ? parseChatDeeplinkFromHref(data.href) : null;
    }
    return {
      chatGroupId: String(data.chatGroupId),
      messageId: data.messageId ? String(data.messageId) : undefined,
      roomId: data.roomId ? String(data.roomId) : undefined,
      companyId: data.companyId ? String(data.companyId) : undefined,
      isInternal: !!(data.isInternal || data.companyId),
    };
  } catch (_) {
    return null;
  }
}

export function attachEmailChatLinkHandler(doc, onOpenChatLink) {
  if (!doc) return () => {};
  const onClick = (event) => {
    const anchor = event.target && event.target.closest && event.target.closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || anchor.href || "";
    if (!href || href.startsWith("mailto:") || href.startsWith("tel:")) return;

    const parsed = parseChatDeeplinkFromHref(href);
    if (parsed && parsed.chatGroupId) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof onOpenChatLink === "function") onOpenChatLink(parsed);
      return;
    }

    if (!isKjuupChatOpenHref(href)) return;
    event.preventDefault();
    event.stopPropagation();
    resolveChatHref(href).then((resolved) => {
      if (resolved && resolved.chatGroupId && typeof onOpenChatLink === "function") {
        onOpenChatLink(resolved);
      }
    });
  };
  doc.addEventListener("click", onClick, true);
  return () => {
    try {
      doc.removeEventListener("click", onClick, true);
    } catch (_) {}
  };
}

/**
 * Same navigation SearchBar uses for a chat search hit: open the room or
 * 1:1 conversation and scroll to messageId when provided.
 */
export function navigateToChatDeeplink({
  dispatch,
  history,
  chatGroupId,
  messageId,
  roomId,
  companyId,
  isInternal,
  page_no,
}) {
  if (!dispatch || !chatGroupId) return;
  const internal = !!(isInternal || companyId);
  dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: internal });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });

  const deeplinkPayload = {
    chatGroupId,
    messageId,
    roomId,
    companyId,
    isInternal: internal,
    page_no: page_no || 1,
  };

  try {
    if (roomId && chatGroupId && !internal) {
      if (history) history.push("/projects");
      dispatch({ type: RIGHT_TOP, rightTop: "" });
      dispatch({ type: TOGGLE_DRAWER, open: false });
      dispatch({ type: SET_DEEPLINK_DATA, payload: null });
      dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
      dispatch({ type: TOGGLE_PROJECT, payload: false });
      dispatch({ type: OPEN_PROJECTS, openProjects: [] });
      dispatch({ type: OPEN_CHATS, openChats: [] });
      dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
      dispatch({ type: SET_IN_ACTIVITY, status: false });
      dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
      dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
      dispatch({ type: RIGHT_TOP, rightTop: "project1" });
      setTimeout(() => {
        dispatch({ type: SET_DEEPLINK_DATA, payload: deeplinkPayload });
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
        dispatch({ type: RIGHT_TOP, rightTop: "project1" });
      }, 1000);
    } else if (roomId && chatGroupId && internal) {
      if (history) history.push("/projects");
      dispatch({ type: SET_DEEPLINK_DATA, payload: null });
      dispatch({ type: RIGHT_TOP, rightTop: "" });
      dispatch({ type: TOGGLE_DRAWER, open: false });
      dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
      dispatch({ type: TOGGLE_PROJECT, payload: false });
      dispatch({ type: OPEN_PROJECTS, openProjects: [] });
      dispatch({ type: OPEN_CHATS, openChats: [] });
      dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
      dispatch({ type: SET_IN_ACTIVITY, status: false });
      dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
      dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
      dispatch({ type: RIGHT_TOP, rightTop: "project1" });
      setTimeout(() => {
        dispatch({ type: SET_DEEPLINK_DATA, payload: deeplinkPayload });
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
        dispatch({ type: RIGHT_TOP, rightTop: "project1" });
      }, 2000);
    } else {
      if (history) history.push("/chat");
      dispatch({ type: RIGHT_TOP, rightTop: "" });
      dispatch({ type: TOGGLE_DRAWER, open: false });
      dispatch({ type: SET_DEEPLINK_DATA, payload: null });
      dispatch({ type: TOGGLE_CHAT, payload: false });
      dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
      dispatch({ type: TOGGLE_PROJECT, payload: false });
      dispatch({ type: OPEN_PROJECTS, openProjects: [] });
      dispatch({ type: OPEN_CHATS, openChats: [] });
      dispatch({ type: SET_IN_ACTIVITY, status: false });
      dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
      dispatch({ type: GROUPS, groups: [] });
      dispatch({ type: SET_CHAT_FULLSCREEN, payload: true });
      dispatch({ type: SET_OPEN_SIDE_BAR, payload: "chat1" });
      dispatch({ type: RIGHT_TOP, rightTop: "chat1" });
      setTimeout(() => {
        dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId, messageId, page_no: page_no || 1 } });
        dispatch({ type: FROM_FROROIEGN_PROFILE, payload: "pageLoad" });
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: "chat1" });
        dispatch({ type: RIGHT_TOP, rightTop: "chat1" });
      }, 1000);
    }
  } catch (error) {
    console.log("[chatDeeplink] navigation error", error);
  }
}
